import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { FleetInspectionService } from './fleet-inspection.service';
import { NotificationService } from 'src/app/shared/notification.service';
import { SendReportService } from 'src/app/widgets/send-report/send-report.service';
import { DomSanitizer } from '@angular/platform-browser';
import {FleetInspection, FleetInspectionField, FleetInspectionFields } from 'src/app/models/fleetinspection';
import { AuthService } from 'src/app/auth.service';
import { SearchService } from 'src/app/search/search.service';

@Component({
  selector: 'app-fleet-inspection',
  templateUrl: './fleet-inspection.component.html',
  styleUrls: ['./fleet-inspection.component.less']
})
export class FleetInspectionComponent implements OnInit{

  @Input() selectedPriceSet: number = 0;
  // @Input() isEditMode: boolean = false;

  @Output() onFleetInspectionClose = new EventEmitter<void>();
  @Output() onFleetInspection = new EventEmitter<void>();

  base64Image: string | ArrayBuffer | null = null;
  base64Doc: string | ArrayBuffer | null = null;

  inspectionDataFields : FleetInspectionField [] = [];

  inspectionData : FleetInspection = new FleetInspection();

  isSaved: boolean = false;
//   timeSinceEmailed: string = '';
  isLoading: boolean = false; // Add isLoading state

//   selectedTab: number = 0; // Default to the first tab
//   completedSteps: boolean[] = [true, false, false];

  uploadingFiles: Set<string> = new Set();
  fileValidationErrors: Map<string, string> = new Map();
  maxFileSize : number = 5 * 1024 * 1024; // 5MB
  allowedFileTypes: string[] = ['image/jpeg', 'image/png', 'application/pdf'];
  inspectionColumns = [] as {fleetInspectionFieldId: number, fleetInspectionFieldName: string}[];
  inspectionRows = [] as {fleetInspectionWheelId: number, fleetInspectionWheelName: string}[];
  fleetCallCentreEmail: string = '';

  constructor(public fleetInspectionService: FleetInspectionService, public notificationService: NotificationService , public sendReportService: SendReportService, public sanitizer: DomSanitizer, public authService: AuthService, private searchService: SearchService) {
    // Initialize the quote object if needed
  }

  ngOnInit(): void {
    this.isLoading = true; // Set loading state to true
    this.fleetCallCentreEmail = this.searchService.lastPriceSet?.callCentreEmail ?? '';

    this.fleetInspectionService.getFleetInspectionData(this.selectedPriceSet).subscribe({
      next: (data) => {
        this.inspectionDataFields = data;
        this.inspectionData.specialPriceSetId = this.selectedPriceSet;
        this.inspectionData.dtCreated = new Date() as unknown as string;
        this.inspectionData.userId = this.authService.user.user_id;
        this.inspectionData.emailAddresses = '';
        this.inspectionData.fields = [];

        this.inspectionColumns = [...new Map(
        this.inspectionDataFields
            .filter(item => item.dataType === 5)
            .map(({fleetInspectionFieldId, fleetInspectionFieldName}) => 
              ({fleetInspectionFieldId, fleetInspectionFieldName})
            )
            .map(item => [JSON.stringify(item), item])
        ).values()];

        this.inspectionRows = [...new Map(
        this.inspectionDataFields
            .filter(item => item.dataType === 5)
            .map(({fleetInspectionWheelId, fleetInspectionWheelName}) => 
              ({fleetInspectionWheelId, fleetInspectionWheelName})
            )
            .map(item => [JSON.stringify(item), item])
        ).values()];

        this.inspectionDataFields.forEach(item => {
            this.inspectionData.fields.push({ ...item });
        });

        this.isLoading = false; 
        this.fleetInspectionService.setFleetInspectionData(this.selectedPriceSet).subscribe({
          next: (inspectionId) => {
            this.inspectionData.fleetInspectionId = inspectionId as number;
          },
          error: (error) => {            
            this.notificationService.showError('Error setting fleet inspection data:', error);
          }
        });
      },
      error: (error) => {
        this.notificationService.showError('Error fetching fleet inspect data:', error);
        this.isLoading = false;
      }
    });
  }

  filterListInspectionData(){
    return this.inspectionDataFields.filter(field => field.dataType !== 5 && field.dataType !== 6);
  }

  filterTableHeaderInspectionData(){
    return this.inspectionDataFields.filter(field => field.dataType === 5);
  }

  filterTableRowInspectionData(){
    return this.inspectionDataFields.filter(field => field.dataType === 6);
  }

  async onFileSelected(event: Event, field: FleetInspectionField): Promise<void> {
    
    this.isLoading = true; 
    const files = (event.target as HTMLInputElement).files;
    
    if (!files || files.length === 0) {
      this.notificationService.showError('No file selected.');
      this.isLoading = false; 
      return;
    }
  
    this.fileValidationErrors.delete(field.fleetInspectionFieldName || '');
  
    const validationResults = this.validateFiles(files, field);
    if (!validationResults.isValid) {
      this.fileValidationErrors.set(field.fleetInspectionFieldName || '', validationResults.error);
      this.clearFileInput(event.target as HTMLInputElement);
      this.isLoading = false; 
      return;
    }
    this.uploadingFiles.add(field.fleetInspectionFieldName || '');
  
    try {
      if (!Array.isArray(field.fleetInspectionFieldValue)) {
        field.fleetInspectionFieldValue = [];
      }
  
      // if (field.dataType === 4 && field.fleetInspectionFieldValueId != null){
      //   await this.loadExistingFieldData(field);
      // }
  
      const processedFiles = await this.processFilesSequentially(files);
      field.fleetInspectionFieldValue.push(...processedFiles);
      this.isSaved = false;
  
      this.notificationService.showSuccess(`${files.length} file(s) uploaded successfully.`);
    } catch (error) {
      this.notificationService.showError('Error uploading files.');
      console.error('Error uploading files:', error);
      this.clearFileInput(event.target as HTMLInputElement);
    } finally {
      this.uploadingFiles.delete(field.fleetInspectionFieldName || '');
      this.isLoading = false; 
    }
  }

  private validateFiles(files: FileList, field: FleetInspectionField): {isValid: boolean, error: string} {
    const currentCount = Array.isArray(field.fleetInspectionFieldValue) ? field.fleetInspectionFieldValue.length : 0;
    const maxFiles = field.dataType === 4 ? 5 : 1; 
  
    if (currentCount + files.length > maxFiles) {
      return {isValid: false,
         error: `You can upload a maximum of ${maxFiles} files for ${field.fleetInspectionFieldName}.`};
    }
  
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
  
      if (file.size > this.maxFileSize) {
        return {isValid: false, 
                error: `File ${file.name} exceeds the maximum size of 5MB.`};
    }
  
    if (!this.allowedFileTypes.includes(file.type)) {
      return {isValid: false, 
              error: `File type of ${file.name} is not allowed. Only JPEG, PNG, and PDF are accepted.`};
    }
  }
   return {isValid: true, error: ''};
  }

  private clearFileInput(input: HTMLInputElement): void {
    input.value = '';
  }

  // private async loadExistingFieldData(field: FleetInspectionField): Promise<void> {
    // try{
    //   const data = await this.fleetInspectionService.getField(field.fleetInspectionFieldValueId).toPromise();
    //   if (data && data.fleetInspectionFieldValue) {
    //     field.fleetInspectionFieldValue = data.fleetInspectionFieldValue;
    //   } else {
    //     field.fleetInspectionFieldValue = [];
    //   }
    // } catch (error) {
    //   console.error('Error loading existing field data:', error);
    // }
  // }

  private async processFilesSequentially(files: FileList): Promise<string[]> {
    const processedFiles: string[] = [];
    for (let i = 0; i < files.length; i++) {
      try {
        const file = files[i];
        let base64String: string;
        if (file.size > 1 * 1024 * 1024) { // Only compress if file is larger than 1MB 
          base64String = await this.compressFile(file);
        } else {
          base64String = await this.fileToBase64(file);
        }
        processedFiles.push(base64String);
      } catch (error) {
        console.error('Error processing file:', error);
      }
    }
    return processedFiles;
  }

  private compressFile(file: File, quality: number = 0.5, maxWidth: number = 1024): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const scaleFactor = maxWidth / img.width;
          canvas.width = maxWidth;
          canvas.height = img.height * scaleFactor;
          const ctx = canvas.getContext('2d');
          if (ctx) {
            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
            const compressedDataUrl = canvas.toDataURL(file.type, quality);
            resolve(compressedDataUrl);
          } else {
            reject(new Error('Canvas context not available'));
          }
        };
        img.onerror = (error) => reject(error);
        img.src = event.target?.result as string;
      };
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(file);
    });
  }

  private fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = () => {
        const result = reader.result as string;
        if (result) {
          resolve(result);
        } else {
          reject(new Error('Failed to read file'));
        }
      };
      
      reader.onerror = () => {
        reject(new Error('File reading failed'));
      };
      
      reader.readAsDataURL(file);
    });
  }
  // Helper method to check if a field is currently uploading
  isFieldUploading(fieldName: string): boolean {
    return this.uploadingFiles.has(fieldName);
  }

  // Helper method to get validation error for a field
  getFieldValidationError(fieldName: string): string | undefined {
    return this.fileValidationErrors.get(fieldName);
  }

  public hasLargeFileFields(): boolean {
    return this.inspectionDataFields.some(
      f => (f.dataType === 3 || f.dataType === 4) && f.fleetInspectionFieldValue?.length > 2
    ) ?? false;
  }

  // openFile(field: FleetInspectionField): void {
  //   this.currentField = field
  //   this.fleetInspectionService.getField(quoteId, field.fleetInspectionFieldValueId).subscribe({
  //     next: (data: FleetInspectionField) => {
  //       this.selectedImages = data.fleetInspectionFieldValue;
  //       this.currentField = data;
  //       this.isImageViewerVisible = true
  //     }
  //     , error: (error) => {
  //       this.notificationService.showError('Failed to fetch image.');
  //     }
  //   });
  // }

  getCellValue(wheelId: number, fieldId: number): string {
    const field = this.inspectionDataFields.find(f => f.fleetInspectionWheelId === wheelId && f.fleetInspectionFieldId === fieldId);
    return field?.fleetInspectionFieldValue ? field.fleetInspectionFieldValue[0] : '';
  }

  onCellValueChange(wheelId: number | null, fieldId: number, newValue: any): void {
    const field = this.inspectionDataFields.find(f => wheelId ? f.fleetInspectionWheelId === wheelId && f.fleetInspectionFieldId === fieldId : f.fleetInspectionFieldId === fieldId);
    if (field) {
      field.fleetInspectionFieldValue = [newValue.toString()];
      this.isSaved = false;
    }
  }

  saveInspection(){
    this.isLoading = true;
    this.inspectionData.fields = this.inspectionDataFields;
    let inspectionDataFields = new FleetInspectionFields();
    inspectionDataFields.fields = this.inspectionDataFields;
    this.fleetInspectionService.saveFleetInspection(this.inspectionData.specialPriceSetId, this.inspectionData.fleetInspectionId, inspectionDataFields).subscribe({
        next: (res) => {
          this.inspectionDataFields = res.fields;
          // this.notificationService.showSuccess('Field value saved successfully.');
          this.fleetInspectionService.emailFleetInspection(this.inspectionData.fleetInspectionId, `${this.fleetCallCentreEmail},${this.authService.user.email}`).subscribe({
            next: () => {
              this.notificationService.showSuccess('Fleet inspection emailed successfully.');
              this.isSaved = true;
              this.isLoading = false;
              this.onFleetInspectionClose.emit();
            },
            error: (error) => {
              this.notificationService.showError('Error emailing fleet inspection:', error);
            }
          });

        },
        error: (error) => {
          this.notificationService.showError('Error saving field value:', error);
          this.isLoading = false;
        }
      });
    }

  areAllRequiredFieldsFilled(): boolean {
    return this.inspectionDataFields.every(field => {
      if (field.isRequired) {
        return field.fleetInspectionFieldValue !== null;
      }
      return true;
    });

  }
}
